import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FAQ_ITEMS } from "@/components/seo/StructuredData";
import Reveal from "@/components/Reveal";

/**
 * Visible FAQ section. Same items as the FAQ JSON-LD schema —
 * AI search engines and human visitors both see consistent, authoritative answers.
 */
export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      data-testid="faq-section"
      className="snap-section md:min-h-screen flex items-center py-20 md:py-24 px-6 md:px-12 lg:px-24 bg-white"
    >
      <div className="max-w-3xl mx-auto w-full">
        <Reveal>
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-3">FAQ</p>
            <h2
              className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#1D1D1F]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Häufige Fragen.
            </h2>
            <p className="mt-4 text-[#86868B] text-base md:text-lg">
              Antworten auf die wichtigsten Fragen rund um Ihre Buchung bei Oona Flats.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="flex flex-col gap-3" itemScope itemType="https://schema.org/FAQPage">
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openIdx === idx;
              return (
                <div
                  key={item.q}
                  data-testid={`faq-item-${idx}`}
                  className="bg-[#FAFAFA] rounded-2xl border border-black/5 overflow-hidden transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.04)]"
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    data-testid={`faq-toggle-${idx}`}
                    onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                    aria-expanded={isOpen}
                  >
                    <h3
                      itemProp="name"
                      className="text-base md:text-lg font-medium text-[#1D1D1F] tracking-tight"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {item.q}
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 shrink-0 text-[#86868B] transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                      style={{ transitionTimingFunction: "var(--ease-apple)" }}
                    />
                  </button>
                  <div
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    className="grid transition-[grid-template-rows] duration-500"
                    style={{
                      gridTemplateRows: isOpen ? "1fr" : "0fr",
                      transitionTimingFunction: "var(--ease-apple)",
                    }}
                  >
                    <div className="overflow-hidden">
                      <p
                        itemProp="text"
                        className="px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-base text-[#1D1D1F]/80 leading-relaxed"
                      >
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
